import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function PortfolioList() {
  const [portfolios, setPortfolios] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState(null);

  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const result = await axios.get(
          `${process.env.REACT_APP_API_URL}portfolio/getAllPortfolio`
        );
        const updatedPortfolio = result.data.map((portfolio) => ({
          ...portfolio,
          image: `${process.env.REACT_APP_API_URL}${portfolio.image.replace(
            /\\/g,
            "/"
          )}`,
        }));
        setPortfolios(updatedPortfolio);
        setFilteredList(updatedPortfolio);
      } catch (error) {
        console.log(error);
        setPortfolios([]);
        setFilteredList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolios();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredList(portfolios);
    } else {
      setFilteredList(
        portfolios.filter(
          (item) =>
            (item.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.category || "").toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, portfolios]);

  const handleOpenDeleteModal = (portfolio) => {
    setPortfolioToDelete(portfolio);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}portfolio/deletePortfolio`,
        { data: { portfolio_id: portfolioToDelete.portfolio_id } }
      );
      setPortfolios(
        portfolios.filter(
          (portfolio) => portfolio.portfolio_id !== portfolioToDelete.portfolio_id
        )
      );
      toast.success("Portfolio deleted successfully", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
      setShowDeleteModal(false);
    } catch (error) {
      console.log(error);
      toast.error("Failed to delete portfolio", {
        position: "top-center",
        autoClose: 2000,
        theme: "colored",
      });
      setShowDeleteModal(false);
    }
  };

  return (
    <div>
      <ToastContainer />

      {/* Main Card Container */}
      <div className="main-card">

        {/* Toolbar Section */}
        <div className="toolbar-container">
          <h2 className="page-title">Portfolio List</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="results-count">{filteredList.length} results</span>
            <input
              type="text"
              className="search-box"
              placeholder="Search by ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Link to="/createPortfolio" className="btn-add-primary">
              + Add New Portfolio
            </Link>
          </div>
        </div>

        {/* Gradient Header */}
        <div className="table-header-panel">
          Portfolio List
        </div>

        {/* Table */}
        <div>
          <table className="custom-table" style={{ tableLayout: 'auto', width: '100%', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px 8px' }}>SR No.</th>
                <th style={{ padding: '10px 8px' }}>ID</th>
                <th style={{ padding: '10px 8px' }}>Title</th>
                <th style={{ padding: '10px 8px' }}>Name</th>
                <th style={{ padding: '10px 8px' }}>Description</th>
                <th style={{ padding: '10px 8px' }}>Category</th>
                <th style={{ padding: '10px 8px' }}>Image</th>
                <th style={{ padding: '10px 8px' }}>URL</th>
                <th style={{ padding: '10px 8px' }}>Sequence</th>
                <th style={{ padding: '10px 8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10">
                    <div className="loading-container">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredList.length === 0 ? (
                <tr>
                  <td colSpan="10">
                    <div className="empty-state">
                      <div className="empty-state-icon">📂</div>
                      <div className="empty-state-text">No portfolios found</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredList.map((portfolio, index) => (
                  <tr key={portfolio.portfolio_id}>
                    <td style={{ padding: '8px' }}>{index + 1}</td>
                    <td style={{ padding: '8px' }}>{portfolio.portfolio_id}</td>
                    <td style={{ padding: '8px', fontWeight: '600' }}>{portfolio.title}</td>
                    <td style={{ padding: '8px' }}>{portfolio.name}</td>
                    <td style={{ padding: '8px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{portfolio.description}</td>
                    <td style={{ padding: '8px' }}>{portfolio.category}</td>
                    <td style={{ padding: '8px' }}>
                      <img
                        src={portfolio.image}
                        alt={portfolio.name}
                        loading="lazy"
                        style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "6px" }}
                      />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <a href={portfolio.url}>View</a>
                    </td>
                    <td style={{ padding: '8px' }}>{portfolio.sequence}</td>
                    <td style={{ padding: '8px' }}>
                      <div className="action-btn-container">
                        <Link
                          to={`/portfolioUpdate/${portfolio.portfolio_id}`}
                          className="btn-action-edit"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleOpenDeleteModal(portfolio)}
                          className="btn-action-delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Portfolio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete portfolio "{portfolioToDelete?.title}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
